import {useCallback, useEffect, useRef} from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import {useLatest} from './use-latest';

export const useResizeObserver = <T extends HTMLElement>(callback: ResizeObserverCallback) => {
    const elementRef = useRef<T | null>(null);
    const observerRef = useRef<ResizeObserver | null>(null);
    const latestCallback = useLatest(callback);

    const subscribe = useCallback(() => {
        const element = elementRef.current;

        if (!element) {
            return;
        }

        let observer = observerRef.current;

        if (!observer) {
            observer = observerRef.current = new ResizeObserver((...args) => {
                latestCallback.current(...args);
            });
        }

        observer.observe(element);
    }, []);

    const unsubscribe = useCallback(() => {
        const element = elementRef.current;
        const observer = observerRef.current;

        if (!element || !observer) {
            return;
        }

        observer.unobserve(element);
    }, []);

    useEffect(
        () => () => {
            observerRef.current?.disconnect();
        },
        [],
    );

    const refCallback = useCallback((element: T | null) => {
        elementRef.current = element;

        if (element instanceof Element) {
            subscribe();
        } else {
            unsubscribe();
        }
    }, []);

    return {refCallback, elementRef};
};
